import React, { useState } from "react";
import axios from "axios";
import { Phone, Mail, MapPin, Send, MessageCircle, Instagram, Facebook } from "lucide-react";
import { useLang } from "@/i18n/LanguageProvider";
import { SectionTitle, Reveal } from "@/components/Reveal";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast, Toaster } from "sonner";
import {
  WA_PHONE, WA_DISPLAY, CONTACT_EMAIL, LOCATION, INSTAGRAM, FACEBOOK, waUrl, BOOK_MESSAGE,
} from "@/lib/contact";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Contact() {
  const { t } = useLang();
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "", message: "" });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill name, email and message.");
      return;
    }
    setBusy(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success(t.contact.success);
      setForm({ name: "", email: "", phone: "", subject: "", message: "" });
    } catch (err) {
      toast.error(t.contact.error);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="page-contact" className="container-px mx-auto pb-24">
      <Toaster position="top-center" richColors />
      <SectionTitle eyebrow={t.contact.eyebrow} title={t.contact.title} subtitle={t.contact.subtitle} />

      <div className="mt-14 grid lg:grid-cols-12 gap-10 items-start">
        {/* Contact info */}
        <Reveal className="lg:col-span-5 space-y-6">
          <div className="rounded-3xl bg-white/70 backdrop-blur border border-white p-7">
            <div className="space-y-5">
              <div className="flex items-start gap-3">
                <MapPin className="w-5 h-5 mt-1 text-moss shrink-0" />
                <div>
                  <div className="eyebrow text-cocoa">Location</div>
                  <div className="text-ink mt-1">{LOCATION}</div>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <Phone className="w-5 h-5 mt-1 text-moss shrink-0" />
                <div>
                  <div className="eyebrow text-cocoa">WhatsApp</div>
                  <a href={`https://wa.me/${WA_PHONE}`} className="text-ink mt-1 block hover:text-moss" data-testid="contact-wa-link">{WA_DISPLAY}</a>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <Mail className="w-5 h-5 mt-1 text-moss shrink-0" />
                <div>
                  <div className="eyebrow text-cocoa">Email</div>
                  <a href={`mailto:${CONTACT_EMAIL}`} className="text-ink mt-1 block hover:text-moss break-all">{CONTACT_EMAIL}</a>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-3 mt-7">
              <a href={waUrl(BOOK_MESSAGE())} target="_blank" rel="noreferrer" data-testid="contact-whatsapp-btn"
                className="inline-flex items-center gap-2 rounded-full bg-wa text-white px-5 py-2.5 text-sm">
                <MessageCircle className="w-4 h-4" /> {t.contact.whatsapp}
              </a>
              <a href={INSTAGRAM} target="_blank" rel="noreferrer" className="w-10 h-10 rounded-full border border-border grid place-items-center hover:bg-sandDeep">
                <Instagram className="w-4 h-4" />
              </a>
              <a href={FACEBOOK} target="_blank" rel="noreferrer" className="w-10 h-10 rounded-full border border-border grid place-items-center hover:bg-sandDeep">
                <Facebook className="w-4 h-4" />
              </a>
            </div>
          </div>

          <div className="overflow-hidden rounded-3xl aspect-[4/3] border border-border">
            <iframe
              title="Agung Wiwin Map"
              src="https://www.google.com/maps?q=Pererenan+Bali&output=embed"
              width="100%"
              height="100%"
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              data-testid="contact-map"
            />
          </div>
        </Reveal>

        {/* Form */}
        <Reveal delay={0.06} className="lg:col-span-7">
          <form onSubmit={submit} data-testid="contact-form" className="rounded-3xl bg-white/70 backdrop-blur border border-white p-7 sm:p-9">
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="eyebrow text-cocoa block mb-2">{t.contact.name}</label>
                <Input data-testid="contact-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required />
              </div>
              <div>
                <label className="eyebrow text-cocoa block mb-2">{t.contact.email}</label>
                <Input type="email" data-testid="contact-email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required />
              </div>
              <div>
                <label className="eyebrow text-cocoa block mb-2">{t.contact.phone}</label>
                <Input data-testid="contact-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
              </div>
              <div>
                <label className="eyebrow text-cocoa block mb-2">{t.contact.subject}</label>
                <Input data-testid="contact-subject" value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} />
              </div>
              <div className="sm:col-span-2">
                <label className="eyebrow text-cocoa block mb-2">{t.contact.message}</label>
                <Textarea data-testid="contact-message" rows={6} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} required />
              </div>
            </div>
            <button
              type="submit"
              disabled={busy}
              data-testid="contact-submit"
              className="mt-7 inline-flex items-center gap-2 rounded-full bg-moss text-white px-6 py-3 text-sm hover:bg-mossLight disabled:opacity-60 shadow-md"
            >
              {busy ? "Sending..." : t.contact.submit} <Send className="w-4 h-4" />
            </button>
          </form>
        </Reveal>
      </div>
    </div>
  );
}
